import { useEffect, useId, useMemo, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { businessProfileSetupGateway } from '../services/BusinessProfileSetupGateway';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatZipDisplay, zipDigitsFromInput } from '../../../shared/formatting';
import { ApiError } from '../../identity/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { Link } from 'react-router-dom';
import { RestrictionLockedDialog, COMPARE_PLANS_PATH } from '../../../shared/LockedFeatureDialog';
import {
  clearSubscriptionRestrictionNotice, restrictionMessage, upgradeRequiredFromError, type SubscriptionRestriction,
} from '../../../shared/subscriptionRestriction';
import { US_STATES } from '../validation';
import {
  TAX_REGISTRATION_LABELS,
  WORK_LOCATION_TYPE,
  WORK_LOCATION_TYPE_LABELS,
  type TaxRegistrationStatusName,
  type WorkLocationResponse,
  type WorkLocationTypeValue,
} from '../types';
import {
  MAX_NOTES_LENGTH,
  emptyForm,
  formFromLocation,
  hasErrors,
  isFormDirty,
  missingRequiredLabels,
  onFileFrom,
  toRequest,
  validateBranchDetails,
  validateRegistration,
  type FormErrors,
  type LocationFormState,
  type RegistrationFormState,
} from './workLocationForm';
import { stateName } from './usStateNames';

type Tab = 'branch' | 'registration';
type Dialog = null | 'clearDraft' | 'goBack' | 'created' | 'conflict';

export interface LocationEditorProps {
  /** The persisted location being edited, or null when adding. */
  existing: WorkLocationResponse | null;
  initialTab?: Tab;
  /** Back to the list. `notice` is shown there (server-confirmed outcomes only). Not used in dialog mode. */
  onBackToList?: (notice?: string) => void;
  /**
   * `page` is Step 5 (the default, unchanged). `dialog` is the Add Employee / employee profile prompt (EPN-233): the same two tabs and the same
   * validation, but no page heading, no Clear Draft and no success card — the prompt closes itself through `onSaved`/`onCancel`.
   */
  mode?: 'page' | 'dialog';
  /** Dialog mode: the server has created the location. */
  onSaved?: (saved: WorkLocationResponse) => void;
  /** Dialog mode: Cancel on the first tab. The caller owns the discard confirmation (it sees `onDirtyChange`). */
  onCancel?: () => void;
  /** Dialog mode: lets the prompt block closing while a save is in flight. */
  onSavingChange?: (saving: boolean) => void;
  /** After a conflict the admin chose to reload: the list is refreshed and this editor reopened on the latest version. */
  onReload: (id: string) => void;
  /** The main location's address comes from Company Address; this jumps to Step 2. */
  onEditMainAddress?: (() => void) | undefined;
  /** Reports whether the form currently differs from what was loaded, so the wizard can guard navigation away from unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
}

const TYPE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: WORK_LOCATION_TYPE.Branch, label: WORK_LOCATION_TYPE_LABELS.Branch ?? 'Branch' },
  { value: WORK_LOCATION_TYPE.RemoteHub, label: WORK_LOCATION_TYPE_LABELS.RemoteHub ?? 'Remote Hub' },
  { value: WORK_LOCATION_TYPE.ClientSite, label: WORK_LOCATION_TYPE_LABELS.ClientSite ?? 'Client Site' },
  { value: WORK_LOCATION_TYPE.Temporary, label: WORK_LOCATION_TYPE_LABELS.Temporary ?? 'Temporary' },
];

const STATUS_OPTIONS: TaxRegistrationStatusName[] = ['Registered', 'InProgress', 'NotRegistered', 'NotRequired'];

/** What the status line under "Registration Status" says. None of it claims a filing, a verification or payroll readiness. */
function statusHint(status: RegistrationFormState['status']): string | null {
  switch (status) {
    case 'NotRegistered': return 'Registration required before this location can process payroll.';
    case 'InProgress': return 'Registration is still in progress. This location is not complete until its account details are on file.';
    case 'NotRequired': return 'Enter the compliance reason in the notes. This stays incomplete until an authorized person verifies it.';
    default: return null;
  }
}

/**
 * Step 5 Add New Location / Edit Location (Figma S20-S29): one form across the two tabs "Branch Details" and "Payroll Tax Registration".
 *
 * Add: blank fields, "Add Branch" (no Save and Exit), Clear Draft with a confirmation, a success card only after the server confirms.
 * Edit: the heading is the location's own name, fields are prefilled, there is no Clear Draft, and Save Changes / Save and Exit stay disabled
 * until a valid change exists anywhere across both tabs (reverting returns it to clean). Go Back from the registration tab returns to Branch
 * Details without a prompt; Go Back from Branch Details with unsaved changes asks Keep Editing / Discard Changes / Save & Go Back.
 *
 * The form is one state object, so switching tabs never loses anything. Saving is blocked until the form is valid; a stale edit is refused by
 * the server (409) and shown as a conflict, never overwritten. Account numbers are never loaded back (only a masked value exists): leaving one
 * empty keeps what is on file.
 */
export function LocationEditor({
  existing, initialTab = 'branch', onBackToList, onReload, onEditMainAddress, onDirtyChange, mode = 'page', onSaved, onCancel, onSavingChange,
}: LocationEditorProps) {
  const uid = useId();
  const inDialog = mode === 'dialog';
  const backToList = (...args: [notice?: string]) => onBackToList?.(...args);
  const isEdit = existing !== null;
  const [current, setCurrent] = useState<WorkLocationResponse | null>(existing);
  const isMain = current?.isDefault ?? false;

  // The prompt exists so the new location can be given to an employee right away, and only an active location can be assigned, so it starts active.
  const initialForm = () => (existing ? formFromLocation(existing) : inDialog ? { ...emptyForm(), activeForPayroll: true } : emptyForm());
  const [baseline, setBaseline] = useState<LocationFormState>(initialForm);
  const [form, setForm] = useState<LocationFormState>(initialForm);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [mailingTab, setMailingTab] = useState<'physical' | 'mailing'>('physical');
  const [showBranchErrors, setShowBranchErrors] = useState(false);
  const [showRegistrationErrors, setShowRegistrationErrors] = useState(false);
  // A field's error shows once the admin has left it (or after a save attempt), so a disabled Add Branch never leaves them guessing.
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const apiErrorRef = useRef<HTMLDivElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [createdActive, setCreatedActive] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  // The server refused activating this location because the plan covers one state (SUBSCRIPTION_UPGRADE_REQUIRED). Nothing the admin typed is
  // lost: the form stays as it is, the explanation opens, and "Save as not active for payroll" re-sends it with ActiveForPayroll=false.
  const [refusal, setRefusal] = useState<{ restriction: SubscriptionRestriction; after: 'stay' | 'list'; canSaveInactive: boolean; dialogOpen: boolean } | null>(null);

  const branchErrors: FormErrors = useMemo(() => validateBranchDetails(form, isMain), [form, isMain]);
  const registrationErrors: FormErrors = useMemo(() => validateRegistration(form, onFileFrom(current)), [form, current]);
  const valid = !hasErrors(branchErrors) && !hasErrors(registrationErrors);
  const dirty = useMemo(() => isFormDirty(form, baseline), [form, baseline]);
  const canSave = valid && !saving && (!isEdit || dirty);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);
  // The prompt scrolls inside its own body, so a refusal below the fold is brought into view rather than left unseen.
  useEffect(() => {
    if (inDialog && apiError) apiErrorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [inDialog, apiError]);
  const missing = missingRequiredLabels(form, isMain);

  const set = <K extends keyof LocationFormState>(key: K, value: LocationFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setNotice(null);
  };
  const setReg = <K extends keyof RegistrationFormState>(key: K, value: RegistrationFormState[K]) => {
    setForm((prev) => ({ ...prev, registration: { ...prev.registration, [key]: value } }));
    setNotice(null);
  };

  const touch = (key: string | null) => {
    if (key) setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };
  const onPanelBlur = (event: FocusEvent<HTMLElement>) => {
    // In the prompt, a field only counts as "left" when focus moves to something else inside the prompt. Focus handed back to the opener behind it
    // (React StrictMode's dev-only effect replay does exactly that on open) is not the admin leaving Branch Name.
    if (inDialog) {
      const prompt = (event.target as HTMLElement).closest('[role="dialog"]');
      if (!prompt || !(event.relatedTarget instanceof Node) || !prompt.contains(event.relatedTarget)) return;
    }
    touch((event.target as HTMLElement).getAttribute('data-field'));
  };
  const branchError = (key: string) => (showBranchErrors || touched.has(key) ? branchErrors[key] : undefined);
  const regError = (key: string) => (showRegistrationErrors || touched.has(key) ? registrationErrors[key] : undefined);
  const id = (name: string) => `${uid}-${name}`;
  const describedBy = (key: string, errorText: string | undefined) => (errorText ? `${id(key)}-error` : undefined);

  const revealErrors = () => {
    setShowBranchErrors(true);
    setShowRegistrationErrors(true);
    if (hasErrors(branchErrors)) setTab('branch');
    else if (hasErrors(registrationErrors)) setTab('registration');
  };

  /** `after` says where the admin goes once the server has confirmed: stay in the editor, back to the list, or (adding) the success card. */
  const save = async (after: 'stay' | 'list', options: { asInactive?: boolean } = {}) => {
    setDialog(null);
    if (!valid) {
      revealErrors();
      return;
    }
    setSaving(true);
    setApiError(null);
    setRefusal(null);
    try {
      if (options.asInactive) set('activeForPayroll', false);
      const request = toRequest(form, current, baseline);
      if (options.asInactive) request.activeForPayroll = false;
      const saved = current
        ? await businessProfileSetupGateway.updateWorkLocation(current.id, request)
        : await businessProfileSetupGateway.createWorkLocation(request);
      const message = saved.duplicateNameWarning ?? (isEdit ? 'Location updated successfully.' : 'Branch/work location added successfully.');
      if (inDialog) {
        onSaved?.(saved);
        return;
      }
      if (!isEdit && after === 'stay') {
        // The success card is only ever shown once the server has saved the location.
        setCreatedActive(saved.activeForPayroll);
        setDialog('created');
        return;
      }
      if (after === 'list') {
        backToList(message);
        return;
      }
      const next = formFromLocation(saved);
      setCurrent(saved);
      setBaseline(next);
      setForm(next);
      setNotice(message);
    } catch (err) {
      const upgrade = upgradeRequiredFromError(err);
      if (upgrade) {
        // The dialog explains it beside the form, so the app-wide banner behind it would only repeat the same refusal.
        clearSubscriptionRestrictionNotice();
        setApiError(restrictionMessage(upgrade));
        setRefusal({ restriction: upgrade, after, canSaveInactive: !isMain && form.activeForPayroll && !options.asInactive, dialogOpen: true });
      } else if (err instanceof ApiError && err.code === 'WORK_LOCATION_VERSION_CONFLICT') {
        setConflictMessage(err.message);
        setDialog('conflict');
      } else {
        setApiError(inDialog
          ? (err instanceof ApiError && err.status === 403
            ? "You don't have permission to add work locations. Ask a company administrator to add it, or choose an existing location."
            : friendlyErrorMessage(err, 'Could not add this work location. Your entries are kept — please try again.'))
          : err instanceof ApiError ? err.message : 'Could not save this location. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (tab === 'registration') {
      setTab('branch');
      return;
    }
    if (inDialog) {
      onCancel?.();
      return;
    }
    if (dirty) setDialog('goBack');
    else backToList();
  };

  const clearDraft = async () => {
    setDialog(null);
    setForm(emptyForm());
    setShowBranchErrors(false);
    setShowRegistrationErrors(false);
    setTouched(new Set());
    setApiError(null);
    setTab('branch');
    setMailingTab('physical');
    try {
      // A stale saved draft from before this version of the form is removed too; failing to do so never blocks clearing the form.
      await businessProfileSetupGateway.clearWorkLocationDraft();
    } catch {
      /* best effort */
    }
  };

  const heading = isEdit ? current?.name ?? existing?.name ?? 'Edit Location' : 'Add New Location';
  const regState = stateName(form.state);
  const hint = statusHint(form.registration.status);
  const mailingOn = !form.mailingSame;

  const field = (key: string, label: string, required: boolean, control: ReactNode, error: string | undefined, help?: string) => (
    <div className="bp-form-group">
      <label htmlFor={id(key)}>
        {label} {required && <span className="req" aria-hidden="true">*</span>}
      </label>
      {control}
      {help && <div className="helper" id={`${id(key)}-help`}>{help}</div>}
      {error && <div className="error" id={`${id(key)}-error`} role="alert">{error}</div>}
    </div>
  );

  const inputProps = (key: string, error: string | undefined, required = false, errorKey: string = key) => ({
    id: id(key),
    'data-field': errorKey,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy(key, error),
  });

  const branchTab = (
    <div role="tabpanel" id={id('panel-branch')} aria-labelledby={id('tab-branch')} onBlur={onPanelBlur}>
      <div className="loc-section-title">Basic Location Details</div>
      {isMain && (
        <div className="notice main-note">
          This is your main location. Its address comes from Company Address and can only be changed there.
          {onEditMainAddress && (
            <button type="button" className="link-btn" onClick={onEditMainAddress}>Edit Company Address</button>
          )}
        </div>
      )}
      <div className="bp-grid">
        {field('name', 'Branch Name', true,
          <input {...inputProps('name', branchError('name'), true)} {...(inDialog ? { 'data-autofocus': true } : {})} className={`bp-input${branchError('name') ? ' invalid' : ''}`} value={form.name}
            maxLength={80} placeholder="e.g. Austin Central Office" onChange={(e) => set('name', e.target.value)} />, branchError('name'))}
        {field('locationType', 'Branch Type', true,
          isMain ? (
            <select {...inputProps('locationType', undefined)} className="bp-select" value="main" disabled>
              <option value="main">Main</option>
            </select>
          ) : (
            <select {...inputProps('locationType', branchError('locationType'), true)} className={`bp-select${branchError('locationType') ? ' invalid' : ''}`}
              value={form.locationType} onChange={(e) => set('locationType', e.target.value === '' ? '' : (Number(e.target.value) as WorkLocationTypeValue))}>
              <option value="">Select Branch</option>
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ), branchError('locationType'))}
      </div>

      <div className="active-payroll-box">
        <div className="toggle-copy">
          <strong id={id('active-label')}>Active for Payroll</strong>
          <span id={id('active-help')}>Employees can be assigned to this location for payroll processing.</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={form.activeForPayroll}
          aria-labelledby={id('active-label')}
          aria-describedby={id('active-help')}
          className={`switch2${form.activeForPayroll ? '' : ' off'}`}
          disabled={isMain}
          onClick={() => set('activeForPayroll', !form.activeForPayroll)}
        />
      </div>

      <div className="loc-section-title">Physical Address</div>
      <div className="address-tabs">
        {/* The "Add Mailing Address" button is not a tab, so it sits beside the tab list rather than inside it (a tablist may only hold tabs). */}
        <div className="address-tab-group">
          <div className="address-tab-list" role="tablist" aria-label="Address">
            <button type="button" role="tab" aria-selected={mailingTab === 'physical'} className={`address-tab${mailingTab === 'physical' ? ' active' : ''}`} onClick={() => setMailingTab('physical')}>
              Physical Address
            </button>
            {mailingOn && (
              <button type="button" role="tab" aria-selected={mailingTab === 'mailing'} className={`address-tab${mailingTab === 'mailing' ? ' active' : ''}`} onClick={() => setMailingTab('mailing')}>
                Mailing Address
              </button>
            )}
          </div>
          {!mailingOn && (
            <button type="button" className="address-tab add" onClick={() => { set('mailingSame', false); setMailingTab('mailing'); }}>
              <i className="fa fa-plus-circle bps-icon" aria-hidden="true" /> Add Mailing Address
            </button>
          )}
        </div>
        <label className="same-address">
          Mailing Address:
          <input type="checkbox" checked={form.mailingSame} onChange={(e) => { set('mailingSame', e.target.checked); if (e.target.checked) setMailingTab('physical'); }} />
          Same as Physical Address
        </label>
      </div>

      {mailingTab === 'physical' ? (
        <div className="address-panel" role="tabpanel">
          <div className="bp-grid">
            {field('addressLine1', 'Address Line 1', true,
              <input {...inputProps('addressLine1', branchError('addressLine1'), true)} className={`bp-input${branchError('addressLine1') ? ' invalid' : ''}`} value={form.addressLine1}
                maxLength={100} placeholder="e.g. 1450 Congress Ave" disabled={isMain} onChange={(e) => set('addressLine1', e.target.value)} />, branchError('addressLine1'))}
            {field('addressLine2', 'Address Line 2', false,
              <input {...inputProps('addressLine2', undefined)} className="bp-input" value={form.addressLine2} maxLength={100} placeholder="e.g. Suite 300"
                disabled={isMain} onChange={(e) => set('addressLine2', e.target.value)} />, undefined)}
            {field('country', 'Country', true,
              <select {...inputProps('country', branchError('country'), true)} className={`bp-select${branchError('country') ? ' invalid' : ''}`} value={form.country}
                disabled={isMain} onChange={(e) => set('country', e.target.value)}>
                <option value="">Select Country</option>
                <option value="US">United States</option>
              </select>, branchError('country'))}
            {field('state', 'State', true,
              <select {...inputProps('state', branchError('state'), true)} className={`bp-select${branchError('state') ? ' invalid' : ''}`} value={form.state}
                disabled={isMain} onChange={(e) => set('state', e.target.value)}>
                <option value="">Select State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{stateName(s)}</option>)}
              </select>, branchError('state'))}
          </div>
          <div className="bp-grid-3">
            {field('city', 'City', true,
              <input {...inputProps('city', branchError('city'), true)} className={`bp-input${branchError('city') ? ' invalid' : ''}`} value={form.city} maxLength={60}
                placeholder="e.g. Palmdale" disabled={isMain} onChange={(e) => set('city', e.target.value)} />, branchError('city'))}
            {field('county', 'County', false,
              <input {...inputProps('county', undefined)} className="bp-input" value={form.county} maxLength={60} placeholder="Optional" disabled={isMain}
                onChange={(e) => set('county', e.target.value)} />, undefined)}
            {field('zip', 'ZIP Code', true,
              <input {...inputProps('zip', branchError('zip'), true)} className={`bp-input${branchError('zip') ? ' invalid' : ''}`} value={formatZipDisplay(zipDigitsFromInput(form.zip))}
                maxLength={10} placeholder="e.g. 78701" inputMode="numeric" disabled={isMain} onChange={(e) => set('zip', zipDigitsFromInput(e.target.value))} />, branchError('zip'))}
          </div>
        </div>
      ) : (
        <div className="address-panel" role="tabpanel">
          <div className="bp-grid">
            {field('mailingLine1', 'Mailing Address Line 1', true,
              <input {...inputProps('mailingLine1', branchError('mailingLine1'), true)} className={`bp-input${branchError('mailingLine1') ? ' invalid' : ''}`} value={form.mailingLine1}
                maxLength={100} placeholder="e.g. PO Box 12" onChange={(e) => set('mailingLine1', e.target.value)} />, branchError('mailingLine1'))}
            {field('mailingLine2', 'Mailing Address Line 2', false,
              <input {...inputProps('mailingLine2', undefined)} className="bp-input" value={form.mailingLine2} maxLength={100} onChange={(e) => set('mailingLine2', e.target.value)} />, undefined)}
            {field('mailingCountry', 'Country', true,
              <select {...inputProps('mailingCountry', branchError('mailingCountry'), true)} className={`bp-select${branchError('mailingCountry') ? ' invalid' : ''}`}
                value={form.mailingCountry} onChange={(e) => set('mailingCountry', e.target.value)}>
                <option value="">Select Country</option>
                <option value="US">United States</option>
              </select>, branchError('mailingCountry'))}
            {field('mailingState', 'State', true,
              <select {...inputProps('mailingState', branchError('mailingState'), true)} className={`bp-select${branchError('mailingState') ? ' invalid' : ''}`}
                value={form.mailingState} onChange={(e) => set('mailingState', e.target.value)}>
                <option value="">Select State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{stateName(s)}</option>)}
              </select>, branchError('mailingState'))}
          </div>
          <div className="bp-grid">
            {field('mailingCity', 'City', true,
              <input {...inputProps('mailingCity', branchError('mailingCity'), true)} className={`bp-input${branchError('mailingCity') ? ' invalid' : ''}`} value={form.mailingCity}
                maxLength={60} onChange={(e) => set('mailingCity', e.target.value)} />, branchError('mailingCity'))}
            {field('mailingZip', 'ZIP Code', true,
              <input {...inputProps('mailingZip', branchError('mailingZip'), true)} className={`bp-input${branchError('mailingZip') ? ' invalid' : ''}`}
                value={formatZipDisplay(zipDigitsFromInput(form.mailingZip))} maxLength={10} inputMode="numeric" onChange={(e) => set('mailingZip', zipDigitsFromInput(e.target.value))} />,
              branchError('mailingZip'))}
          </div>
          <div className="helper">The mailing address is for correspondence only. The physical address decides the tax jurisdiction.</div>
        </div>
      )}
    </div>
  );

  const reg = form.registration;
  const onFile = current?.taxRegistration;
  const accountPlaceholder = (masked: string | null | undefined, example: string) => (masked ? `On file ${masked}. Type to replace.` : example);

  const registrationTab = (
    <div role="tabpanel" id={id('panel-registration')} aria-labelledby={id('tab-registration')} onBlur={onPanelBlur}>
      <div className="loc-section-title">State Payroll Tax Registration{regState ? ` (${regState})` : ''}</div>
      <div className="bp-grid">
        {field('regStatus', 'Registration Status', true,
          <select {...inputProps('regStatus', regError('status'), true, 'status')} className={`bp-select${regError('status') ? ' invalid' : ''}`} value={reg.status}
            onChange={(e) => setReg('status', e.target.value as RegistrationFormState['status'])}>
            <option value="">Select status</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{TAX_REGISTRATION_LABELS[s]}</option>)}
          </select>, regError('status'))}
        {field('effectiveDate', 'Tax Registration Effective Date', false,
          <input {...inputProps('effectiveDate', regError('effectiveDate'))} className={`bp-input${regError('effectiveDate') ? ' invalid' : ''}`} type="date" value={reg.effectiveDate}
            onChange={(e) => setReg('effectiveDate', e.target.value)} />, regError('effectiveDate'),
          `Date your ${form.state || 'state'} payroll tax registration takes effect.`)}
      </div>
      {hint && <div className={`status-hint${reg.status === 'NotRegistered' ? ' warn' : ''}`} role="status"><i className="fa fa-info-circle bps-icon" aria-hidden="true" /> {hint}</div>}
      <div className="bp-grid">
        {field('stateAccount', 'SEIN', false,
          <input {...inputProps('stateAccount', regError('stateAccount'))} className={`bp-input${regError('stateAccount') ? ' invalid' : ''}`} value={reg.stateAccount} autoComplete="off"
            placeholder={accountPlaceholder(onFile?.stateAccountNumberMasked, 'e.g. 694-0000-0')} onChange={(e) => setReg('stateAccount', e.target.value)} />, regError('stateAccount'))}
        {field('nexusDate', 'Nexus Established Date', false,
          <input {...inputProps('nexusDate', regError('nexusEstablishedDate'), false, 'nexusEstablishedDate')} className={`bp-input${regError('nexusEstablishedDate') ? ' invalid' : ''}`} type="date" value={reg.nexusEstablishedDate}
            onChange={(e) => setReg('nexusEstablishedDate', e.target.value)} />, regError('nexusEstablishedDate'),
          `Date the company first had payroll nexus in ${regState ? 'this state' : 'the state'}.`)}
      </div>

      <div className="loc-section-title">Unemployment and Disability Insurance</div>
      <div className="bp-grid">
        {field('suiAccount', 'SUI Account Number', false,
          <input {...inputProps('suiAccount', regError('suiAccount'))} className={`bp-input${regError('suiAccount') ? ' invalid' : ''}`} value={reg.suiAccount} autoComplete="off"
            placeholder={accountPlaceholder(onFile?.suiAccountNumberMasked, 'e.g. 123-4567-8')} onChange={(e) => setReg('suiAccount', e.target.value)} />, regError('suiAccount'))}
        {field('sdiAccount', 'SDI / PFL Account Number (if applicable)', false,
          <input {...inputProps('sdiAccount', regError('sdiPflAccount'), false, 'sdiPflAccount')} className={`bp-input${regError('sdiPflAccount') ? ' invalid' : ''}`} value={reg.sdiPflAccount} autoComplete="off"
            placeholder={accountPlaceholder(onFile?.sdiPflAccountNumberMasked, 'e.g. 694-0000-0-0')} onChange={(e) => setReg('sdiPflAccount', e.target.value)} />, regError('sdiPflAccount'))}
      </div>

      <div className="loc-section-title">Local Tax <span className="optional">(if applicable)</span></div>
      <div className="bp-grid">
        {field('localReg', 'Local Tax Registration', false,
          <input {...inputProps('localReg', regError('localRegistration'), false, 'localRegistration')} className={`bp-input${regError('localRegistration') ? ' invalid' : ''}`} value={reg.localRegistration} autoComplete="off"
            placeholder={accountPlaceholder(onFile?.localTaxRegistrationMasked, 'e.g. 123-4567-8')} onChange={(e) => setReg('localRegistration', e.target.value)} />, regError('localRegistration'))}
        {field('localAccount', 'Local Tax Account Number', false,
          <input {...inputProps('localAccount', regError('localAccount'))} className={`bp-input${regError('localAccount') ? ' invalid' : ''}`} value={reg.localAccount} autoComplete="off"
            placeholder={accountPlaceholder(onFile?.localTaxAccountNumberMasked, 'e.g. 694-0000-0-0')} onChange={(e) => setReg('localAccount', e.target.value)} />, regError('localAccount'))}
      </div>

      <div className="loc-section-title">Notes / Compliance Remarks <span className="optional">(optional)</span></div>
      <div className="bp-form-group">
        <label htmlFor={id('notes')} className="sr-only">Notes / Compliance Remarks</label>
        <textarea
          {...inputProps('notes', regError('notes'))}
          className={`bp-textarea${regError('notes') ? ' invalid' : ''}`}
          value={reg.notes}
          maxLength={MAX_NOTES_LENGTH}
          placeholder="Add notes about the registration process, pending taxes, or any compliance-related remarks"
          onChange={(e) => setReg('notes', e.target.value)}
        />
        <div className="char-count" aria-live="polite">{reg.notes.length}/{MAX_NOTES_LENGTH} characters</div>
        {regError('notes') && <div className="error" id={`${id('notes')}-error`} role="alert">{regError('notes')}</div>}
      </div>
      <div className="helper">Account numbers are stored securely and shown only as their last four characters. Tax rates are set in Payroll Settings, not here.</div>
    </div>
  );

  return (
    <div className="location-form loc-editor">
      {!inDialog && <div className="form-title">{heading}</div>}
      <div className="loc-tabs" role="tablist" aria-label="Location sections">
        <button type="button" role="tab" id={id('tab-branch')} aria-selected={tab === 'branch'} aria-controls={id('panel-branch')} className={`loc-tab${tab === 'branch' ? ' active' : ''}`} onClick={() => setTab('branch')}>
          Branch Details
        </button>
        <button type="button" role="tab" id={id('tab-registration')} aria-selected={tab === 'registration'} aria-controls={id('panel-registration')} className={`loc-tab${tab === 'registration' ? ' active' : ''}`} onClick={() => setTab('registration')}>
          Payroll Tax Registration
        </button>
      </div>

      {tab === 'branch' ? branchTab : registrationTab}

      {notice && <div className="notice success-note" role="status">{notice}</div>}
      {apiError && <div ref={apiErrorRef} className="notice error-note" role="alert">{apiError}</div>}
      {refusal && (
        <div className="notice loc-upgrade-actions" data-testid="location-upgrade-required">
          <p>
            Everything you entered is kept.
            {refusal.canSaveInactive && ' You can save this location as not active for payroll now and activate it once your plan covers it.'}
          </p>
          <div className="loc-upgrade-actions__row">
            {refusal.canSaveInactive && (
              <button type="button" className="bp-btn bp-btn-secondary" disabled={saving} onClick={() => void save(refusal.after, { asInactive: true })}>
                Save as not active for payroll
              </button>
            )}
            <Link className="bp-btn bp-btn-secondary" to={COMPARE_PLANS_PATH}>Compare plans</Link>
          </div>
        </div>
      )}
      {refusal?.dialogOpen && (
        <RestrictionLockedDialog
          restriction={refusal.restriction}
          fallbackFeatureName="Multi-state payroll"
          onClose={() => setRefusal((r) => (r ? { ...r, dialogOpen: false } : r))}
          extraActions={refusal.canSaveInactive ? (
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => void save(refusal.after, { asInactive: true })}>
              Save as not active for payroll
            </button>
          ) : undefined}
        />
      )}

      <div className="loc-editor-footer">
        <div className="footer-left">
          {!isEdit && !inDialog && (
            <button type="button" className="bp-btn bp-btn-clear" onClick={() => setDialog('clearDraft')}>
              <i className="fa fa-trash bps-icon" aria-hidden="true" />
              Clear Draft
            </button>
          )}
        </div>
        <div className="footer-right">
          <button type="button" className="bp-btn bp-btn-secondary" onClick={goBack} disabled={saving}>
            {inDialog && tab === 'branch' ? 'Cancel' : 'Go Back'}
          </button>
          {isEdit && (
            <button type="button" className="bp-btn bp-btn-draft" disabled={!canSave} onClick={() => void save('list')}>
              Save and Exit
            </button>
          )}
          <button
            type="button"
            className="bp-btn bp-btn-primary"
            disabled={!canSave}
            aria-describedby={!isEdit && !valid ? id('save-hint') : undefined}
            onClick={() => void save('stay')}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Branch'}
          </button>
        </div>
      </div>
      {!isEdit && !valid && (
        <span className="sr-only" id={id('save-hint')}>
          Add Branch is available once every required field is complete{missing.length > 0 ? `: ${missing.join(', ')}` : ''}.
        </span>
      )}

      <ConfirmDialog
        open={dialog === 'clearDraft'}
        title="Discard this Draft?"
        body="All information you've entered will be cleared and cannot be recovered."
        confirmLabel="Discard Draft"
        cancelLabel="Keep Editing"
        destructive
        onCancel={() => setDialog(null)}
        onConfirm={() => void clearDraft()}
      />
      <ConfirmDialog
        open={dialog === 'goBack'}
        title="Save Before Leaving?"
        body="You have unsaved information. Would you like to save your progress before going back?"
        confirmLabel="Save & Go Back"
        cancelLabel="Keep Editing"
        tertiaryLabel="Discard Changes"
        onTertiary={() => backToList()}
        onCancel={() => setDialog(null)}
        onConfirm={() => void save('list')}
      />
      <ConfirmDialog
        open={dialog === 'created'}
        tone="success"
        hideCancel
        title="Branch Created Successfully!"
        body={createdActive
          ? 'The new branch has been saved and is now active. You can view or edit it anytime from the branch list.'
          : 'The new branch has been saved as inactive. Turn on Active for Payroll when it is ready for employee assignments. You can view or edit it anytime from the branch list.'}
        confirmLabel="Go to Branch List"
        onCancel={() => backToList('Branch/work location added successfully.')}
        onConfirm={() => backToList('Branch/work location added successfully.')}
      />
      <ConfirmDialog
        open={dialog === 'conflict'}
        title="This Location Was Changed"
        body={conflictMessage}
        confirmLabel="Reload Latest Version"
        cancelLabel="Keep Editing"
        onCancel={() => setDialog(null)}
        onConfirm={() => { if (current) onReload(current.id); }}
      />
    </div>
  );
}
